import { Component, Input, OnInit, ElementRef, ViewChild } from '@angular/core';
import { TestComponent } from '../../components/test/test.component';
import { QuestionComponent } from '../../question/question/question.component';
import { QuestionService } from '../../services/question.service';
import { Evaluation, Question } from '../../interfaces/questionInterface';
import { lastValueFrom, timer, take } from 'rxjs';
import { AnswerComponent } from '../../question/answer/answer.component';
import { ModalComponent } from '../../components/modal/modal.component';
import { Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { AchievementService } from '../../services/achievement.service';
import { NgIconComponent } from '@ng-icons/core';
import { Achievement } from '../../interfaces/achievementInterface';
import { Chance } from 'chance';

@Component({
    selector: 'app-endless-execution',
    imports: [TestComponent, QuestionComponent, AnswerComponent, ModalComponent, DatePipe, NgIconComponent, RouterLink],
    templateUrl: './endless-execution.component.html',
    styleUrl: './endless-execution.component.scss'
})
export class EndlessExecutionComponent implements OnInit {
  public courseId!: string;
  public activeQuestion?: Question;
  public flagged: boolean = false;
  public currentAnswers: string[] = [];

  public evaluation?: Evaluation;
  public index = 1;
  
  public totalCount = 0;
  public correctCount = 0;
  public totalElapsedSeconds = 0;

  public streakProgress: {current: number, next: number} = {current: 0, next: 0};
  public achievement?: {achievement: Achievement, nextCount?: number};

  private activeQuestionSelectedTime: Date = new Date();

  public progressGemSize = "20px";

  public orderSeed: string = new Chance().string({length: 16});

  @Input()
  set id(courseId: string) {
    this.courseId = courseId;
  }

  @ViewChild(TestComponent)
  private testComponent!: TestComponent;

  @ViewChild('submitWarningModal')
  private submitWarningModal!: ModalComponent;

  @ViewChild('resultModal')
  private resultModal!: ModalComponent;

  @ViewChild('progressbar', { read: ElementRef })
  private progressbar!: ElementRef<HTMLDivElement>;

  @ViewChild('achievementModal')
  private achievementModal!: ModalComponent;

  currentResponseId: string = '';

  constructor(
    private router: Router,
    private questionService: QuestionService,
    private achievementService: AchievementService
  ) { }

  ngOnInit(): void {
    this.questionService.getEndlessQuestion(this.courseId).then(question => {
      this.activeQuestion = question;
      
      this.questionService.getOneFlaggedQuestion(this.activeQuestion.id).then(flagged => {
        if (flagged) {
          this.flagged = true;
        }
      });
    });

    this.achievementService.getCorrectStreakProgress().then(progress => {
      this.streakProgress = progress;
      this.updateProgressBar();
    });
  }

  async nextQuestion(): Promise<void> {
    await this.testComponent.fadeOut();

    this.activeQuestion = await this.questionService.getEndlessQuestion(this.courseId);
    this.evaluation = [];
    this.index++;
    this.activeQuestionSelectedTime = new Date();
    this.flagged = false;

    this.questionService.getOneFlaggedQuestion(this.activeQuestion.id).then(flagged => {
      if (flagged) {
        this.flagged = true;
      }
    }
    )

    await this.testComponent.fadeIn();
  }

  async submitAnswer(): Promise<void> {
    if (!this.activeQuestion) return;

    const elapsedSeconds = (new Date().getTime() - this.activeQuestionSelectedTime.getTime()) / 1000;
    this.totalCount++;
    this.totalElapsedSeconds += elapsedSeconds;

    const result = await this.questionService.submitEndlessAnswer(this.activeQuestion.id, this.currentAnswers, elapsedSeconds, true, this.orderSeed);
    this.evaluation = result.evaluation;
    this.currentAnswers = [];
    this.currentResponseId = result.responseId;

    if(this.evaluation?.every(e => e.hit)) {
      this.correctCount++;

      this.streakProgress.current++;
      if(this.streakProgress.current === this.streakProgress.next) {
        const achievement = await this.achievementService.claimStreakAchievement();
        if(achievement){
          this.achievement = achievement;
          this.toggleAchievementModal();
          this.achievementService.getCorrectStreakProgress().then(progress => {
            this.streakProgress = progress;
            this.updateProgressBar();
          });
        }
      }
    } else {
      this.streakProgress.current = 0;
    }

    this.updateProgressBar();
  }

  handleAnswersSelected(answers: string[]): void {
    this.currentAnswers = answers;
  }

  async handleQuestionFlagged(): Promise<void> {
    this.flagged = !this.flagged;

    if (this.flagged && this.activeQuestion) {
      await this.questionService.flagQuestion(this.activeQuestion.id);
    } else if (this.activeQuestion){
      await this.questionService.unflagQuestion(this.activeQuestion.id);
    }
  }

  updateProgressBar() {
    const progress = this.streakProgress.current / this.streakProgress.next * 100;
    this.progressbar.nativeElement.style.width = `${progress}%`;

    this.progressGemSize = `${20 + (30 - 20) * progress / 100}px`;
  }

  toggleSubmitWarningModal(): void {
    this.submitWarningModal.toggleModal();
  }

  toggleResultModal(): void {
    this.resultModal.toggleModal();
  }

  toggleAchievementModal() {
    this.achievementModal.toggleModal();
  }
}
